/* eslint-disable react-hooks/exhaustive-deps */
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ApprovalStatusLabel } from '../../../../../../../api/enumerations';
import { getOperationFlow } from '../../../../../../../api/theHive/licensedCompanies';
import { OperationFlowItem } from '../../../../../../../api/theHive/licensedCompanies/types';
import { formatCnpjCpf, formatDate } from '../../../../../../../helpers';
import useErrorMessage from '../../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../../hooks/useSnackbar';
import {
  StyledCell,
  StyledPagination,
  StyledRow,
  TableCellHeader,
} from '../styles';
import { AnalysisStatusIcon } from './AnalysisStatusIcon';

const PER_PAGE = 100;

export function OperationFlow(): JSX.Element {
  const [items, setItems] = useState<OperationFlowItem[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();
  const navigate = useNavigate();
  const { notice: noticeIdParam } = useParams();
  const noticeId = parseInt(noticeIdParam as string, 10);

  // Página vem da URL (?page=) para sobreviver ao voltar da análise.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  const handleNavigate = (companyId: number): void => {
    navigate(`./request/${companyId}`);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(value));
      return next;
    });
  };

  const fetchOperationFlow = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await getOperationFlow(noticeId, page, PER_PAGE, [
        ApprovalStatusLabel.PENDING,
        ApprovalStatusLabel.AWAITING_FORMALIZATION,
        ApprovalStatusLabel.FORMALIZATION_SUBMITTED,
      ]);
      if (response instanceof Error) throw response;
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      setItems(response.data ?? []);
      setTotalPages(response.detail.total_pages ?? 1);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!Number.isNaN(noticeId)) fetchOperationFlow();
  }, [noticeId, page]);

  const renderRows = (): JSX.Element => {
    if (loading) {
      return (
        <StyledRow>
          <StyledCell colSpan={5} align="center">
            <CircularProgress size={24} />
          </StyledCell>
        </StyledRow>
      );
    }

    if (items.length === 0) {
      return (
        <StyledRow>
          <StyledCell colSpan={5} align="center">
            Nenhum pedido cadastrado.
          </StyledCell>
        </StyledRow>
      );
    }

    return (
      <>
        {items.map((item) => (
          <StyledRow
            key={item.notice_company_id}
            onClick={() => handleNavigate(item.notice_company_id)}
          >
            <StyledCell>{item.company_name}</StyledCell>
            <StyledCell align="center">{formatCnpjCpf(item.cnpj)}</StyledCell>
            <StyledCell align="center">
              {item.requested_at ? formatDate(item.requested_at) : '—'}
            </StyledCell>
            <StyledCell align="center">
              <AnalysisStatusIcon status={item.juridico_status} />
            </StyledCell>
            <StyledCell align="center">
              <AnalysisStatusIcon status={item.engenharia_status} />
            </StyledCell>
          </StyledRow>
        ))}
      </>
    );
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCellHeader width="30%">Nome da empresa</TableCellHeader>
              <TableCellHeader align="center" width="22%">
                CNPJ
              </TableCellHeader>
              <TableCellHeader align="center" width="22%">
                Data do pedido
              </TableCellHeader>
              <TableCellHeader align="center" width="13%">
                Jurídico
              </TableCellHeader>
              <TableCellHeader align="center" width="13%">
                Engenharia
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>{renderRows()}</TableBody>
        </Table>
      </TableContainer>
      <StyledPagination
        page={page}
        count={totalPages}
        onChange={handlePageChange}
      />
    </>
  );
}
